import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import SwiperCore, { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { addTranslation, IntlProps } from 'decorators/addTranslation';
import I18nHelpers from 'i18n/I18nHelpers';
import Icon from 'components/ui/icon';
import { confirmationTypes } from '../types';
import './confirmSlider.scss';
import 'swiper/swiper-bundle.css';
import googleplayImage from 'images/social/google-play.svg';
import appstoreImage from 'images/social/app-store.svg';
import googleAuthImage from 'images/google-auth.png';

import step4Image from './images/step-4.svg';
import step5Image from './images/step-5.svg';
import step6Image from './images/step-6.svg';
import { CopyString } from '../../../../ui/copyString/CopyString';
import classNames from 'classnames';

interface ConnectedProps {
  interfaceLanguage: string;
}
interface OwnProps {
  qrImageBase64: string;
  secret: string;
  type: confirmationTypes;
}

type Props = OwnProps & ConnectedProps & IntlProps;

class ConfirmSlider extends PureComponent<Props> {
  render() {
    const { type } = this.props;
    SwiperCore.use([Navigation, Pagination]);
    return (
      <div className='confirm-slider'>
        <Swiper
          spaceBetween={16}
          slidesPerView={1}
          loopedSlides={2}
          centeredSlides
          loop
          navigation={{
            prevEl: '.confirm-slider__arrow_prev',
            nextEl: '.confirm-slider__arrow_next',
          }}
          pagination={{
            clickable: true,
            el: '.confirm-slider__pagination',
            bulletClass: 'confirm-slider__bullet',
            bulletActiveClass: 'confirm-slider__bullet_active',
          }}>
          {this.commonSteps}
          {type === confirmationTypes.SMSCode ? (
            <>
              {this.step4}
              {this.step5}
              {this.finalStep}
            </>
          ) : (
            <>{this.step4Qr}</>
          )}
        </Swiper>
        <div className='confirm-slider__navigation'>
          <button
            className='confirm-slider__arrow confirm-slider__arrow_prev'
            type='button'>
            <Icon name='im-Arrow-left-Option-2' size={12} />
          </button>
          <button
            className='confirm-slider__arrow confirm-slider__arrow_next'
            type='button'>
            <Icon name='im-Arrow-right-Option-2' size={12} />
          </button>
        </div>
        <div className='confirm-slider__pagination' />
      </div>
    );
  }

  getTextBlock(step) {
    const stepTextClass = 'confirm-slider__text_' + step;
    return (
      <div className='confirm-slider__content'>
        <div className='confirm-slider__title'>
          {this.translate(step, 'header')}
        </div>
        <div className={classNames('confirm-slider__text', stepTextClass)}>
          {this.translate(step, 'text')}
        </div>
      </div>
    );
  }

  translate(step, part: 'text' | 'header') {
    const { getTranslate, type } = this.props;
    return I18nHelpers.getJsx(
      getTranslate(
        `auth.login.2faActivation.step${step}.${
          type === confirmationTypes.QRCode ? 'qr.' : ''
        }${part}`
      )
    );
  }

  get commonSteps() {
    const { interfaceLanguage } = this.props;
    const customClass = 'confirm-slider__item_' + interfaceLanguage;
    return (
      <>
        <SwiperSlide className='confirm-slider__item'>
          <div className='confirm-slider__img'>
            <img
              src={googleplayImage}
              alt='googleplay'
              className='confirm-slider__pic confirm-slider__pic_store'
            />
            <img
              src={appstoreImage}
              alt='appstore'
              className='confirm-slider__pic confirm-slider__pic_store'
            />
          </div>
          {this.getTextBlock(1)}
        </SwiperSlide>
        <SwiperSlide className='confirm-slider__item'>
          <div className='confirm-slider__img'>
            <img
              src={googleAuthImage}
              alt='ga'
              className='confirm-slider__pic confirm-slider__pic_ga'
            />
          </div>
          {this.getTextBlock(2)}
        </SwiperSlide>
        <SwiperSlide
          className={classNames('confirm-slider__item', customClass)}>
          {this.getTextBlock(3)}
        </SwiperSlide>
      </>
    );
  }

  get step4() {
    return (
      <SwiperSlide className='confirm-slider__item'>
        <div className='confirm-slider__img'>
          <img src={step4Image} alt='' className='confirm-slider__pic' />
        </div>
        {this.getTextBlock(4)}
      </SwiperSlide>
    );
  }

  get step5() {
    return (
      <SwiperSlide className='confirm-slider__item'>
        <div className='confirm-slider__img'>
          <img src={step5Image} alt='' className='confirm-slider__pic' />
        </div>
        {this.getTextBlock(5)}
      </SwiperSlide>
    );
  }

  get finalStep() {
    const isSms = this.props.type === confirmationTypes.SMSCode;
    return (
      <SwiperSlide className='confirm-slider__item'>
        <div className='confirm-slider__img'>
          <img src={step6Image} alt='' className='confirm-slider__pic' />
        </div>
        {this.getTextBlock(isSms ? 6 : 5)}
      </SwiperSlide>
    );
  }

  get step4Qr() {
    const { qrImageBase64, secret } = this.props;
    return (
      <SwiperSlide className='confirm-slider__item'>
        {this.getTextBlock(4)}
        <div className='confirm-slider__img confirm-slider__img_qr'>
          <img
            className='confirm-slider__pic'
            src={`data:image/png;base64,${qrImageBase64}`}
            alt='auth-qr-code'
          />
          <CopyString text={secret} customClass='confirm-2fa__secret' />
        </div>
      </SwiperSlide>
    );
  }
}

const mapStateToProps = (state): ConnectedProps => ({
  interfaceLanguage: state.user?.interfaceLang ?? '',
});

export default connect(mapStateToProps)(addTranslation(ConfirmSlider));
